// Utility functions for input validation to ensure only English characters are accepted

export const validateEnglishInput = (value: string): boolean => {
  // Allow English letters, numbers, spaces, and common punctuation
  const englishRegex = /^[a-zA-Z0-9\s@._-]*$/;
  return englishRegex.test(value);
};

export const validateNumericInput = (value: string): boolean => {
  // Allow only numbers and decimal point
  const numericRegex = /^[0-9.]*$/;
  return numericRegex.test(value);
};

export const validatePhoneInput = (value: string): boolean => {
  // Allow only numbers, spaces, +, -, (, )
  const phoneRegex = /^[0-9\s+\-()]*$/;
  return phoneRegex.test(value);
};

export const validateEmailInput = (value: string): boolean => {
  // Standard email validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(value);
};

export const sanitizeInput = (value: string): string => {
  // Remove any non-English characters and keep only allowed ones
  return value.replace(/[^a-zA-Z0-9\s@._-]/g, '');
};

export const sanitizeNumericInput = (value: string): string => {
  // Keep only numbers and decimal point
  return value.replace(/[^0-9.]/g, '');
};

export const sanitizePhoneInput = (value: string): string => {
  // Keep only numbers, spaces, +, -, (, )
  return value.replace(/[^0-9\s+\-()]/g, '');
};

// Input handlers for form fields
export const handleEnglishInput = (e: React.ChangeEvent<HTMLInputElement>): string => {
  const value = e.target.value;
  if (validateEnglishInput(value)) {
    return value;
  }
  return sanitizeInput(value);
};

export const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>): string => {
  const value = e.target.value;
  if (validateNumericInput(value)) {
    return value;
  }
  return sanitizeNumericInput(value);
};

export const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>): string => {
  const value = e.target.value;
  if (validatePhoneInput(value)) {
    return value;
  }
  return sanitizePhoneInput(value);
};

// Format phone number for display
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Return 10-digit number without country code
  if (phoneNumber.length <= 10) {
    return phoneNumber;
  } else if (phoneNumber.length === 11 && phoneNumber.startsWith('0')) {
    return phoneNumber.substring(1);
  } else if (phoneNumber.length === 12 && phoneNumber.startsWith('91')) {
    return phoneNumber.substring(2);
  }
  
  return phoneNumber;
};

// Format currency for display
export const formatCurrency = (amount: number, locale: string = 'en-IN'): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Format number with proper decimal places
export const formatNumber = (value: number, decimals: number = 2): string => {
  return value.toFixed(decimals);
};
